import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { FontLineHeightNormal, Spacing4, Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import type { ProductTabDescriptionModel } from 'autogen/swagger/all';
import { CustomMediaContentButton } from 'autogen/translation-keys/trans-website-product-tabs';
import { Button } from 'components/Button';
import useIsOverflowing from 'hooks/globals/useIsOverflowing';
import { verticalEllipsis } from 'modules/helpers/mixins';
import Accordion from '../../shared/Accordion';
import BlockList from '../blockList';
import Highlights from '../highlights';
import Table from '../table';
import descriptionTableStyling from '../table/styles';

const previewLines = 3;

const DescriptionWrapper = styled.div`
    padding-bottom: ${Spacing8};

    ${descriptionTableStyling}
`;

const Content = styled.div<{ $collapsed: boolean }>`
    ${({ $collapsed }) =>
        $collapsed &&
        css`
            ${verticalEllipsis(previewLines)}
            height: ${previewLines * FontLineHeightNormal}rem;
        `}
    margin-bottom: ${Spacing4};
`;

const TableWrapper = styled.div<{ $hide: boolean }>`
    // iOS fix, table is overlapping text if accordion is collapsed.
    ${({ $hide }) => $hide && 'opacity: 0;'}
    ${({ $hide }) => $hide && 'pointer-events: none;'}
`;

const StyledButton = styled(Button)`
    margin: 0 0 ${Spacing16};
`;

export default function Description({ ...props }: ProductTabDescriptionModel): JSX.Element {
    const [collapsed, setCollapsed] = useState(true);
    const contentRef = useRef<HTMLDivElement>(null);
    const isOverflowing = useIsOverflowing(contentRef, false);
    const { blockList, tableModel, highlightModel, hasCustomMediaContent, instructions } = props;
    const expandedBlockList = {
        ...blockList,
        blocks: blockList.blocks.map((block) => ({ ...block, showAllText: true }))
    };
    const { t } = useTranslation();

    return (
        <DescriptionWrapper>
            {highlightModel && <Highlights {...highlightModel} />}

            {isOverflowing ? (
                <Accordion collapsed={collapsed} setCollapsed={setCollapsed} previewLines={previewLines}>
                    <Content $collapsed={collapsed}>
                        {hasCustomMediaContent && (
                            <StyledButton
                                variant="secondary"
                                forwardedAs="a"
                                href="#customMediaContentSection"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.getElementById('customMediaContentSection')?.scrollIntoView({
                                        behavior: 'smooth'
                                    });
                                }}
                            >
                                {t(CustomMediaContentButton)}
                            </StyledButton>
                        )}
                        <BlockList {...expandedBlockList} />
                        {instructions?.length > 0 && (
                            <StyledButton
                                variant="secondary"
                                forwardedAs="a"
                                href={instructions[0].url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {instructions[0].text}
                            </StyledButton>
                        )}
                        <TableWrapper $hide={collapsed}>
                            {tableModel && <Table {...tableModel} type="Description" />}
                        </TableWrapper>
                    </Content>
                </Accordion>
            ) : (
                <Content $collapsed ref={contentRef}>
                    {hasCustomMediaContent && (
                        <StyledButton
                            variant="secondary"
                            forwardedAs="a"
                            href="#customMediaContentSection"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById('customMediaContentSection')?.scrollIntoView({
                                    behavior: 'smooth'
                                });
                            }}
                        >
                            {t(CustomMediaContentButton)}
                        </StyledButton>
                    )}
                    <BlockList {...expandedBlockList} />
                    {instructions?.length > 0 && (
                        <StyledButton
                            variant="secondary"
                            forwardedAs="a"
                            href={instructions[0].url}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {instructions[0].text}
                        </StyledButton>
                    )}
                    {tableModel && <Table {...tableModel} type="Description" />}
                </Content>
            )}
        </DescriptionWrapper>
    );
}
